import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../hooks/useTranslation';
import { getCurrentLanguage, LANGUAGE_CHANGE_EVENT } from '../config/languageConfig';
import { searchAlternatives, AlternativeItem, MenuAlternativeConfig, BrandAlternativeConfig } from '../config/searchAlternatives';
import { useAddress } from '../contexts/AddressContext';
import { searchRestaurantsByMenu } from '../api/menuService';

interface NoSearchResultsProps {
  searchTerm: string;
  searchType: 'MENU' | 'FRANCHISE';
  onRetry?: () => void;
}

const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  searchTerm,
  searchType,
  onRetry
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { address } = useAddress();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, []);

  // 대체 추천 데이터 가져오기
  const alternativeConfig = searchAlternatives[searchType.toLowerCase() as 'menu' | 'brand']?.[searchTerm];

  const handleAlternativeClick = async (alternative: AlternativeItem) => {
    // 위치 정보가 없는 경우 파라미터로만 이동
    if (!address?.lat || !address?.lng) {
      const params = new URLSearchParams();
      
      if (alternative.menuName) {
        params.append('menuName', alternative.menuName);
      }
      
      if (alternative.categoryId) {
        params.append('categoryId', alternative.categoryId);
      }
      
      if (alternative.franchiseId) {
        params.append('franchiseId', alternative.franchiseId);
      }
      
      navigate(`/search-results?${params.toString()}`);
      return;
    }

    try {
      // API를 호출하여 실제 메뉴 탐색 수행
      if (alternative.menuName) {
        // 메뉴명으로 검색
        await searchRestaurantsByMenu(
          address.lat,
          address.lng,
          alternative.menuName,
          alternative.categoryId,
          address.addressId // addressId 추가
        );
        
        // API 호출 후 검색 결과 페이지로 이동
        const params = new URLSearchParams();
        params.append('menuName', alternative.menuName);
        
        if (alternative.categoryId) {
          params.append('categoryId', alternative.categoryId);
        }
        
        navigate(`/search-results?${params.toString()}`);
      } else {
        // 프랜차이즈 검색의 경우 기존 로직 유지
        const params = new URLSearchParams();
        
        if (alternative.franchiseId) {
          params.append('franchiseId', alternative.franchiseId);
        }
        
        navigate(`/search-results?${params.toString()}`);
      }
    } catch (error) {
      console.error('Error searching with alternative:', error);
      // 에러 발생 시에도 검색 결과 페이지로 이동
      const params = new URLSearchParams();
      
      if (alternative.menuName) {
        params.append('menuName', alternative.menuName);
      }
      
      if (alternative.categoryId) {
        params.append('categoryId', alternative.categoryId);
      }
      
      if (alternative.franchiseId) {
        params.append('franchiseId', alternative.franchiseId);
      }
      
      navigate(`/search-results?${params.toString()}`);
    }
  };

  const handleBrowseGuides = () => {
    navigate('/guides');
  };

  const handleExploreRestaurants = () => {
    navigate('/browse');
  };

  // 다양한 스킨톤의 🙏 이모지 6개
  const prayingEmojis = ['🙏🏻', '🙏🏼', '🙏🏽', '🙏🏾', '🙏🏿', '🙏'];

  // 메뉴/브랜드 타입에 따른 대체 옵션 처리
  const getAlternatives = (): AlternativeItem[] => {
    if (!alternativeConfig) return [];
    
    if (searchType === 'MENU') {
      const menuConfig = alternativeConfig as MenuAlternativeConfig;
      return menuConfig.alternatives;
    } else {
      const brandConfig = alternativeConfig as BrandAlternativeConfig;
      return [brandConfig.alternative];
    }
  };

  const alternatives = getAlternatives();

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-8 bg-gray-50">
      {/* Material Design Card Container */}
      <div className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        
        {/* 이모지 섹션 */}
        <div className="px-6 pt-8 pb-2">
          <div className="flex justify-center space-x-2 mb-4">
            {prayingEmojis.map((emoji, index) => (
              <span key={index} className="text-2xl">
                {emoji}
              </span>
            ))}
          </div>
        </div>

        {/* "No ..." 메시지 - Material Design Body1 */}
        <div className="px-6 pb-4">
          <div className="text-center">
            <p className="text-base font-normal text-gray-600 leading-6">
              {t('search.noResults.mainMessage.before')}
              <span className="text-gray-900 font-medium">'{searchTerm}'</span>
              {t('search.noResults.mainMessage.after')}
            </p>
          </div>
        </div>

        {/* 대체 추천이 있는 경우 - 메인 콘텐츠 */}
        {alternativeConfig && (
          <div className="px-6 pb-6">
            {/* 추천 이유 메시지 - Material Design Headline6 */}
            <div className="mb-6 text-center">
              <h1 className="text-xl font-medium text-gray-900 leading-7 tracking-wide">
                {alternativeConfig.reason[currentLang]}
              </h1>
            </div>
            
            {/* 대체 키워드 버튼들 - Material Design Contained Buttons */}
            <div className="space-y-3 mb-8">
              {alternatives.map((alternative: AlternativeItem, index: number) => (
                <button
                  key={index}
                  onClick={() => handleAlternativeClick(alternative)}
                  className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-center rounded-lg font-medium text-base tracking-wide transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-md hover:shadow-lg active:shadow-sm transform active:scale-[0.98]"
                >
                  <span>{alternative.text[currentLang]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider - Material Design */}
        <div className="border-t border-gray-200 mx-6 mb-6"></div>

        {/* Action Buttons */}
        <div className="px-6 pb-8">
          {/* Browse Guides - Material Design Outlined Button */}
          <button
            onClick={handleBrowseGuides}
            className="w-full px-6 py-3 bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-700 hover:text-gray-900 font-medium text-base rounded-lg border border-gray-300 hover:border-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 shadow-sm hover:shadow-md"
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{t('search.noResults.actions.browseOtherGuides')}</span>
            </div>
          </button>

          {/* Explore Restaurants (대체 키워드가 없는 경우만) - Material Design Contained Button */}
          {!alternativeConfig && (
            <button
              onClick={handleExploreRestaurants}
              className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-medium text-base rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-md hover:shadow-lg active:shadow-sm transform active:scale-[0.98] mt-4"
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t('search.noResults.actions.exploreNearby')}</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoSearchResults; 